// No-Show Tracking Types

// Must match backend PenaltyType enum in Models/Entities/NoShow.cs
export enum PenaltyType {
  None = 'None',
  Warning = 'Warning',
  DepositRequired = 'DepositRequired',
  BookingRestriction = 'BookingRestriction',
  Banned = 'Banned'
}

export interface NoShow {
  id: number;
  bookingId: number;
  userId: string;
  customerName: string;
  customerEmail: string;
  barbershopId: number;
  barbershopName: string;
  scheduledDateTime: string;
  markedNoShowAt: string;
  markedByUserId: string;
  markedByName: string;
  reason?: string;
  notes?: string;
  wasDepositRequired: boolean;
  depositAmount: number;
  depositForfeited: boolean;
  penaltyApplied: boolean;
  penaltyType: PenaltyType;
  penaltyAppliedAt?: string;
  penaltyExpiresAt?: string;
  disputeRaised: boolean;
  disputeReason?: string;
  disputeResolved: boolean;
  customerNotified: boolean;
}

// Matches backend CustomerNoShowHistoryDto
export interface CustomerNoShowHistory {
  userId: string;
  customerName: string;
  customerEmail: string;
  totalNoShows: number;
  noShowsLast30Days: number;
  noShowsLast90Days: number;
  noShowsLast365Days: number;
  noShowRate: number; // Percentage
  totalDepositForfeited: number;
  totalBookings: number;
  lastNoShowDate?: string;
  firstNoShowDate?: string;
  currentlyPenalized: boolean;
  currentPenaltyType: PenaltyType;
  penaltyExpiresAt?: string;
  depositRequired: boolean;
  recommendedDepositAmount: number;
  noShows: NoShow[];
  riskLevel: string; // "Low" | "Medium" | "High"
  generatedAt: string;
}

export interface NoShowStatistics {
  totalNoShows: number;
  noShowRate: number; // Percentage
  noShowsThisMonth: number;
  noShowsLastMonth: number;
  noShowGrowthPercentage: number;
  averageNoShowsPerDay: number;
  topOffenders: CustomerNoShowSummary[];
  barbershopBreakdown: BarbershopNoShowStats[];
  monthlyTrend: MonthlyNoShowTrend[];
  timeOfDayAnalysis: TimeOfDayNoShowStats[];
  estimatedRevenueLoss: number;
}

export interface CustomerNoShowSummary {
  userId: string;
  userName: string;
  userEmail: string;
  totalNoShows: number;
  noShowRate: number;
  currentPenalty: PenaltyType;
  lastNoShowDate: string;
  totalBookings: number;
}

export interface BarbershopNoShowStats {
  barbershopId: number;
  barbershopName: string;
  totalNoShows: number;
  noShowRate: number;
  totalBookings: number;
  estimatedRevenueLoss: number;
}

export interface MonthlyNoShowTrend {
  month: number;
  monthName: string;
  year: number;
  noShowCount: number;
  totalBookings: number;
  noShowRate: number;
}

export interface TimeOfDayNoShowStats {
  timeSlot: string; // e.g., "Morning (6-12)", "Afternoon (12-18)", "Evening (18-22)"
  noShowCount: number;
  totalBookings: number;
  noShowRate: number;
}

export interface NoShowRate {
  userId: string;
  totalBookings: number;
  noShowCount: number;
  noShowRate: number; // Percentage
  riskLevel: string;
  currentPenalty?: PenaltyType;
  penaltyStatus?: PenaltyType; // Deprecated - use currentPenalty instead
}

export interface MarkNoShowDto {
  bookingId: number;
  reason?: string;
  notes?: string;
  notifyCustomer: boolean;
}

export interface ApplyPenaltyDto {
  userId: string;
  penaltyType: PenaltyType;
  reason: string;
  expiresAt?: string;
}

export interface NoShowFilters {
  barbershopId?: number;
  userId?: string;
  startDate?: string;
  endDate?: string;
  page?: number;
  pageSize?: number;
}
